import { useEffect } from "react";

interface UseWistiaPlayerProps {
  videoId: string;
  shouldLoadPlayer: boolean;
  deferUntilInteraction: boolean;
  startMuted: boolean;
  onAspectRatioChange: (ratio: number | null) => void;
}

export default function useWistiaPlayer({
  videoId,
  shouldLoadPlayer,
  deferUntilInteraction,
  startMuted,
  onAspectRatioChange
}: UseWistiaPlayerProps) {
  useEffect(() => {
    if (!shouldLoadPlayer || !videoId) {
      return;
    }

    const wistiaWindow = window as any;
    wistiaWindow._wq = wistiaWindow._wq || [];

    const handleReady = (video: any) => {
      if (deferUntilInteraction) {
        video.play();
        if (startMuted) {
          video.muted(true);
        }
      }

      if (video.captions) {
        video.captions.hide();
      }

      try {
        let width: number | null = null;
        let height: number | null = null;

        const mediaData = video.data?.media;
        if (
          mediaData &&
          typeof mediaData.width === "number" &&
          typeof mediaData.height === "number" &&
          mediaData.width > 0 &&
          mediaData.height > 0
        ) {
          width = mediaData.width;
          height = mediaData.height;
        } else if (typeof video.width === "function" && typeof video.height === "function") {
          width = video.width();
          height = video.height();
        }

        if (width && height && width > 0 && height > 0) {
          const ratio = width / height;
          onAspectRatioChange(ratio);
        } else {
          onAspectRatioChange(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to calculate aspect ratio:", error);
        onAspectRatioChange(null);
      }
    };

    wistiaWindow._wq.push({
      id: videoId,
      onReady: handleReady
    });
  }, [videoId, shouldLoadPlayer, deferUntilInteraction, startMuted, onAspectRatioChange]);
}
